import { appState } from '../../../../../interfaces/common';
import { Flex, Grid, Icon, Link, Option, OrderedList, Select, Text } from '@pega/cosmos-react-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { LanguageLocalizationLink } from '../../../../../constants/common';
import { languageMapping } from '../../../../../constants/languageCodes';
import useLocalTranslation from '../../../../../hooks/useLocalTranslation';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../../redux/actions';
import { useSupportedLanguagesSelector } from '../../../../../redux/selectors';
import CustomTooltip from '../../../../Utilities/CustomTooltip';
import { updateUIToggle } from '../../ChatApis';
import DisplayToggles from '../DisplayToggles';
import CustomLocalization from './CustomLocalization';
import RealTimeTranslation from './RealTimeTranslation';

const LanguageTab: React.FC = () => {
  const { sessionId } = useSelector((state: appState) => {
    return {
      accounts: state.accounts,
      sessionId: state.sessionId,
    };
  });

  const {
    connectionId,
    primaryLanguage,
    languageTranslationEnabled = false,
    translateFromLanguages = [],
    translationServiceId,
  } = useSelector((state: appState) => state.chatInfo);
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();

  const [translateToLang, setTranslateToLang] = useState('');
  const [translationEnablerError, setTranslationEnablerError] = useState(false);
  const items = [
    translate('menu_selection'),
    translate('preferred_lang_context'),
    translate('browser_lang'),
    translate('primary_business_lang'),
  ];
  const { configRecords } = useSelector((state: appState) => state);

  useEffect(() => {
    if (primaryLanguage) {
      setTranslateToLang(primaryLanguage);
    }
  }, [primaryLanguage]);

  const updateTranslateToLang = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const data = e.target.value;
    if (data === 'empty') {
      return;
    }
    setTranslateToLang(data);
    if (data !== primaryLanguage) {
      await updateTranslateConfig(
        'third_party_translation',
        { primaryLanguage: e.target.value, type: 'Translate_To' },
        'translation_success_primary_lang',
      );
    }
  };

  const updateTranslateConfig = async (togglePath: string, payload: any, bannerParam: string) => {
    dispatch(updateLoaderDetails(true));
    let json = await updateUIToggle(togglePath, sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(bannerParam), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate(`${bannerParam}_update_failed`), 'urgent'));
    }
  };

  const languagesSupportedByConsumer = useSupportedLanguagesSelector();

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2.5,
      }}
    >
      <Flex>
        <Text variant="h1">{translate('general_language_configurations')}</Text>
      </Flex>
      <Link href={LanguageLocalizationLink} target="_blank" variant="link">
        {translate('configure_language_and_localization')}
        <Icon name="open" />
      </Link>
      <Flex container={{ direction: 'column', itemGap: 1.5 }}>
        <Text variant="h3">
          {translate('translate_to')}
          <CustomTooltip
            info={translate('translate_to_tooltip')}
            iconName="help"
            tooltipStyle={{ marginLeft: '5px' }}
          />
        </Text>
        <Grid
          container={{
            cols: '33%',
            gap: 1,
            alignItems: 'end',
          }}
        >
          <Select
            value={translateToLang}
            onChange={updateTranslateToLang}
            status={translationEnablerError && !translateToLang ? 'error' : undefined}
          >
            <Option key="uniquesKey2" value="empty">
              {translate('primary_lang_selection')}
            </Option>
            {languagesSupportedByConsumer?.reduce((translateToLanguageList: any, language: any) => {
              /** Prevent showing any invalid language code sent by platform */
              if (languageMapping[language]) {
                translateToLanguageList.push(
                  <Option value={language} key={language}>
                    {languageMapping[language]}
                  </Option>,
                );
              }
              return translateToLanguageList;
            }, [])}
          </Select>
          {translationEnablerError && !translateToLang && (
            <Text status="error">{translate('primary_lang_selection')}</Text>
          )}
        </Grid>
      </Flex>
      <Flex container={{ direction: 'column', itemGap: 4 }}>
        <Flex item={{ grow: 0.4 }}>{connectionId && <DisplayToggles displayTitle={false} />}</Flex>
      </Flex>
      <Flex container={{ direction: 'column', itemGap: 1.5 }}>
        <Text variant="h3">
          {translate('order_of_consideration')}
          <CustomTooltip
            info={translate('order_of_consideration_tooltip')}
            iconName="help"
            tooltipStyle={{ marginLeft: '5px' }}
          />
        </Text>
        <Flex item={{ grow: 3 }} style={{ fontWeight: 0 }}>
          <OrderedList items={items} count={items.flat().length} style={{ paddingInlineStart: 0 }} />
        </Flex>
      </Flex>
      <CustomLocalization />
      {configRecords && Object.keys(configRecords).length > 0 && (
        <RealTimeTranslation
          translationEnablerError={translationEnablerError}
          setTranslationEnablerError={setTranslationEnablerError}
          connectionId={connectionId}
          translateToLanguage={translateToLang}
          languageTranslationEnabled={languageTranslationEnabled}
          translateFromLanguages={translateFromLanguages}
          translationServiceId={translationServiceId}
          showLink={true}
        />
      )}
    </Flex>
  );
};

export default LanguageTab;
